/**
 * §4.2's group-buying deals, and the shop's own live commitments.
 *
 * A deal card has to say four things, and the fourth is what makes somebody add
 * a case: how many have joined, what the price is now, when it closes, and **how
 * many more unlock the next price**. A progress bar without that last sentence
 * is decoration.
 *
 * Counts, never names. A merchant learns that eleven shops are buying, never
 * which — telling them would hand every wholesaler on the platform a map of the
 * local trade.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Users } from "lucide-react";

import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Pill,
  SkeletonRows,
} from "@/components/ui";
import { formatDate, formatMoney, plural } from "@/lib/format";
import {
  marketplaceRepository,
  type BuyingPool,
} from "@/repositories/marketplaceRepository";

import { RetailerMark } from "./MarketplaceShowcase";

export function GroupBuys() {
  const pools = useQuery({
    queryKey: ["marketplace", "group-buys"],
    queryFn: () => marketplaceRepository.pools(),
  });

  const mine = useQuery({
    queryKey: ["marketplace", "my-group-buys"],
    queryFn: () => marketplaceRepository.myPools(),
  });

  return (
    <div className="space-y-5">
      {mine.data && mine.data.length > 0 && (
        <Card className="space-y-3">
          <h2 className="text-sm font-semibold text-ink-900">
            You are waiting on
          </h2>
          <ul className="divide-y divide-line">
            {mine.data.map((commitment) => (
              <li
                key={commitment.id}
                className="flex items-center justify-between py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm text-ink-900">
                    {commitment.quantity} x {commitment.pool.listing.name}
                  </p>
                  <p className="text-xs text-ink-500">
                    Closes {formatDate(commitment.pool.closes_at)} ·{" "}
                    {commitment.pool.progress.merchant_count} shops so far
                  </p>
                </div>
                <Pill tone="info">
                  {formatMoney(commitment.pool.progress.current_unit_price)}
                </Pill>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {pools.isError ? (
        <ErrorState
          title="Group buys could not load"
          onRetry={() => pools.refetch()}
        />
      ) : pools.isLoading ? (
        <SkeletonRows rows={4} />
      ) : pools.data && pools.data.results.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {pools.data.results.map((pool) => (
            <PoolCard key={pool.id} pool={pool} />
          ))}
        </div>
      ) : (
        <EmptyState
          title="No group buys open"
          description="Schedule any bulk-priced product and one starts for that day."
        />
      )}
    </div>
  );
}

function PoolCard({ pool }: { pool: BuyingPool }) {
  const client = useQueryClient();
  const { progress } = pool;

  const join = useMutation({
    mutationFn: () =>
      marketplaceRepository.joinPool(pool.id, {
        quantity: pool.listing.minimum_order_quantity,
      }),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: ["marketplace", "group-buys"],
      });
      void client.invalidateQueries({
        queryKey: ["marketplace", "my-group-buys"],
      });
    },
  });

  // Against the target where the wholesaler set one, otherwise against the next
  // rung — a consolidation has no headline number and inventing one would
  // suggest it can fail, which it cannot.
  const goal = progress.target_quantity ?? progress.next_tier_quantity;
  const percent = goal
    ? Math.min(100, Math.round((progress.committed_quantity / goal) * 100))
    : 100;

  return (
    <Card className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink-900">
            {pool.listing.name}
          </p>
          <p className="text-xs text-ink-500">
            <RetailerMark supplier={pool.listing.wholesaler} />
          </p>
        </div>
        <Pill tone={pool.origin === "SUPPLIER" ? "warn" : "info"}>
          {pool.origin === "SUPPLIER" ? "Deal" : "Group buy"}
        </Pill>
      </div>

      {pool.headline && (
        <p className="text-sm text-money-700">{pool.headline}</p>
      )}

      <div>
        <div className="h-2 overflow-hidden rounded-full bg-canvas">
          <div
            className="h-full rounded-full bg-money-500"
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-xs text-ink-500">
          <span className="flex items-center gap-1">
            <Users className="size-3.5" aria-hidden />
            {plural(progress.merchant_count, "shop")} ·{" "}
            {progress.committed_quantity} packs
          </span>
          <span className="flex items-center gap-1">
            <Clock className="size-3.5" aria-hidden />
            closes {formatDate(pool.closes_at)}
          </span>
        </div>
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-lg font-semibold tabular-nums text-money-700">
          {formatMoney(progress.current_unit_price)}
        </span>
        {Number(progress.saving_per_pack) > 0 && (
          <span className="text-xs text-ink-500 line-through">
            {formatMoney(progress.base_unit_price)}
          </span>
        )}
      </div>

      {/* The sentence that adds a case to somebody's order. */}
      {progress.to_next_tier && progress.next_tier_unit_price && (
        <p className="rounded-control bg-info-50 p-2 text-xs text-info-500">
          {plural(progress.to_next_tier, "more pack")} unlocks{" "}
          {formatMoney(progress.next_tier_unit_price)} each for everybody.
        </p>
      )}

      <Button
        variant="secondary"
        fullWidth
        loading={join.isPending}
        onClick={() => join.mutate()}
      >
        Join this group buy
      </Button>
    </Card>
  );
}
